import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Navigator } from '@/presentation/routing/Navigator';
import type { AppDispatch } from '@/presentation/store';
import { setLoading, setFirebaseUserId, setUser } from '@/presentation/store/slices/auth/authSlice';
import { onAuthStateChangedUseCase } from '@/domain/usecases/auth/onAuthStateChangedUseCase';

interface RootScreenProps {
  children: React.ReactNode;
}

/**
 * RootScreen - Firebase Auth State Listener
 * 
 * This component ONLY tracks Firebase authentication state (user ID).
 * It does NOT fetch full user data or trigger initialization.
 * 
 * Responsibilities:
 * - Listen to Firebase auth state changes
 * - Update Redux with firebaseUserId (or null if logged out)
 * - Set loading state
 * 
 * Initialization is handled by:
 * - AppInitializer: For app restart with existing auth
 * - LoginPage: For new login
 */
export const RootScreen = ({ children }: RootScreenProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Initialize Navigator once when RootScreen mounts
  useEffect(() => {
    Navigator.setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    // Subscribe to Firebase auth state changes
    // Callback receives only the Firebase user ID (not full user object)
    const unsubscribe = onAuthStateChangedUseCase.execute(async (firebaseUserId) => {
      dispatch(setLoading(false));

      if (!firebaseUserId) {
        // User logged out - clear both auth state and user cache
        dispatch(setFirebaseUserId(null));
        dispatch(setUser(null));
        return;
      }

      // User authenticated - update Redux with Firebase user ID
      // AppInitializer will handle initialization on app restart
      // LoginPage will handle initialization on new login
      dispatch(setFirebaseUserId(firebaseUserId));
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [dispatch]);

  return <>{children}</>;
};

export default RootScreen;